import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { MapRoute, Marker } from 'src/app/models/localization/map-route';
import { CombosConfiguration, MessageService } from 'brainloper-ui';
import { Subscription } from 'rxjs';

declare var google: any; // Declaración para acceder a la API de Google Maps

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: false
})
export class MapComponent implements OnInit, AfterViewInit {
  @ViewChild(MapInfoWindow, { static: false }) infoWindow!: MapInfoWindow;
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChildren(MapMarker) mapMarkers!: QueryList<MapMarker>;
  @ViewChild('searchBox', { static: false }) searchBoxRef!: ElementRef;

  @Output() routerData = new EventEmitter<MapRoute>();
  @Input() markers: Marker[] = [];
  @Input() routerFinished: boolean = false;
  @Input() searchLocation: boolean = true;

  public center: google.maps.LatLngLiteral = { lat: 5.30933, lng: -73.81575 };
  public zoom = 8;

  //public markers: Marker[] = [];
  public circles: {
    center: google.maps.LatLngLiteral;
    radius: number;
    options?: google.maps.CircleOptions;
  }[] = [];
  private radius = 1500; // Radio en metros

  public mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
  };

  private directionsService = new google.maps.DirectionsService();
  public directionsResults: google.maps.DirectionsResult | undefined = null;
  private tempSelectedMarkerOrder: string | null = null;
  private travelTime: string | undefined;
  private travelDistance: string | undefined;
  public selectedMarkerIndex: number | null = null;
  public selectedMarkerName: string = '';
  private infoWindowOpen: boolean = false;
  private currentStep = 0;
  private closeClickSubscription: Subscription | null = null;

  public reloadComboMarker = new EventEmitter<any>();
  public combosConfigMarker: CombosConfiguration = {
    visibleField: 'order',
    selectionField: 'id',
    label: 'Orden',
  };

  constructor(private message: MessageService) {}

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    if (this.searchLocation) {
      this.initAutocomplete();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers'] && this.markers && this.markers.length > 0) {
      this.routerFinished = true;
      this.calculateRoute(false);
    }
  }

  initAutocomplete(): void {
    const input = this.searchBoxRef.nativeElement as HTMLInputElement;
    const searchBox = new google.maps.places.SearchBox(input);

    // Ensure the map is initialized
    if (this.map && this.map.googleMap) {
      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces();
        if (places.length === 0) {
          return;
        }

        // For each place, get the icon, name, and location.
        const bounds = new google.maps.LatLngBounds();
        places.forEach((place) => {
          if (!place.geometry || !place.geometry.location) {
            console.log('Returned place contains no geometry');
            return;
          }

          // Only adjust the map bounds without adding a marker
          if (place.geometry.viewport) {
            bounds.union(place.geometry.viewport);
          } else {
            bounds.extend(place.geometry.location);
          }
        });
        this.map.googleMap.fitBounds(bounds);
      });
    }
  }

  addMarker(event: google.maps.MapMouseEvent) {
    if (this.routerFinished == true) {
      return;
    }
    if (event.latLng && !this.infoWindowOpen) {
      const newMarker: Marker = {
        position: event.latLng.toJSON(),
        option: { draggable: true },
      };

      let newIndex: number;
      const markerTitles = ['Punto Origen', 'Punto Destino'];

      if (this.markers.length < 2) {
        newMarker.id = markerTitles[this.markers.length];
        newMarker.order = newMarker.id;
        newIndex = this.markers.length;
        this.markers.push(newMarker);
        this.addCircle(newMarker.position, this.markers.length);
      } else {
        const positionToAddData: number = Math.floor(this.markers.length - 1);
        newMarker.id = `Punto de Control ${positionToAddData}`;
        newMarker.order = newMarker.id;
        newIndex = positionToAddData;
        this.markers.splice(positionToAddData, 0, newMarker);
        this.addCircle(newMarker.position, positionToAddData);
      }

      this.reloadComboMarker.emit();

      setTimeout(() => {
        this.openInfoWindow(event, newIndex, true);
      }, 0);
    }
  }

  markerDragEnd(event: google.maps.MapMouseEvent, index: number) {
    if (event.latLng) {
      this.markers[index].position = event.latLng.toJSON();
      this.circles[index].center = event.latLng.toJSON();
    }
  }

  addCircle(center: google.maps.LatLngLiteral, index: number) {
    const newCircle: {
      center: google.maps.LatLngLiteral;
      radius: number;
      options?: google.maps.CircleOptions;
    } = {
      center,
      radius: this.radius,
      options: {
        fillColor: 'blue',
        fillOpacity: 0.35,
        strokeColor: 'blue',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        zIndex: 1,
      },
    };
    if (index === 0) {
      this.circles.push(newCircle);
    } else {
      this.circles.splice(index, 0, newCircle);
    }
  }

  async calculateRoute(
    saveRoute: boolean = true,
    reverseRoute: boolean = false
  ) {
    if (this.markers.length < 2) {
      await this.message.Warning(
        'Alerta',
        'Se necesitan al menos dos marcadores para generar una ruta.'
      );
      this.markers.length == 0
        ? this.message.Info('', 'Escoja el punto de origen', false, 'Continuar')
        : this.message.Info(
            '',
            'Escoja el punto de destino',
            false,
            'Continuar'
          );
      return;
    }

    const waypoints: google.maps.DirectionsWaypoint[] = this.markers
      .slice(1, -1)
      .map((marker) => ({
        location: marker.position,
        stopover: true,
      }));

    const request: google.maps.DirectionsRequest = {
      origin: this.markers[0].position,
      destination: this.markers[this.markers.length - 1].position,
      waypoints: waypoints,
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode.DRIVING,
    };

    this.directionsService.route(request, (result: any, status: any) => {
      if (status === google.maps.DirectionsStatus.OK && result) {
        this.directionsResults = result;
        this.routerFinished = true;

        let totalDistance: number = 0;
        let totalTime: number = 0;
        result.routes[0].legs.forEach((leg: any) => {
          totalDistance += leg.distance.value; // en metros
          totalTime += leg.duration.value; // en segundos
        });

        if (reverseRoute) {
          totalDistance *= 2;
          totalTime *= 2;
        }

        this.travelDistance = (totalDistance / 1000).toFixed(2) + ' km';
        this.travelTime = (totalTime / 3600).toFixed(2) + ' horas';

        const data: MapRoute = {
          markers: this.markers,
          travelTime: this.travelTime,
          travelDistance: this.travelDistance,
          saveRoute: saveRoute,
        };
        this.routerData.emit(data);        
      }
    });
  }

  async calculateReverseRoute(saveRoute: boolean = true) {
    // Crear una copia de los marcadores en orden inverso
    // const reversedMarkers = [...this.markers].reverse();
    // const waypoints: google.maps.DirectionsWaypoint[] = reversedMarkers.slice(1, -1).map(marker => ({
    //   location: marker.position,
    //   stopover: true
    // }));
    // const request: google.maps.DirectionsRequest = {
    //   origin: reversedMarkers[0].position,
    //   destination: reversedMarkers[reversedMarkers.length - 1].position,
    //   waypoints: waypoints,
    //   optimizeWaypoints: false,
    //   travelMode: google.maps.TravelMode.DRIVING
    // };
    // this.directionsService.route(request, (result: any, status: any) => {
    //   if (status === google.maps.DirectionsStatus.OK) {
    //     this.directionsResults = result;
    //     this.routerFinished = true;
    //     let totalDistance: number = 0;
    //     let totalTime: number = 0;
    //     result.routes[0].legs.forEach((leg: any) => {
    //       totalDistance += leg.distance.value; // en metros
    //       totalTime += leg.duration.value; // en segundos
    //     });
    //     this.travelDistance = (totalDistance / 1000).toFixed(2) + ' km';
    //     this.travelTime = (totalTime / 3600).toFixed(2) + ' horas';
    //     const data: MapRoute = {
    //       markers: reversedMarkers,
    //       travelTime: this.travelTime,
    //       travelDistance: this.travelDistance,
    //       saveRoute: saveRoute
    //     };
    //     this.routerData.emit(data);
    //   }
    // });
  }

  openInfoWindow(
    event: google.maps.MapMouseEvent,
    index: number,
    setId: boolean = false
  ) {
    this.infoWindowOpen = true;
    this.combosConfigMarker.selected = this.markers[index].id;
    this.reloadComboMarker.emit('selected');
    this.selectedMarkerIndex = index;

    if (setId) {
      this.selectedMarkerName = '';
    } else {
      this.selectedMarkerName = this.markers[index].id;
    }

    const mapMarker: MapMarker = this.mapMarkers.toArray()[index];
    this.infoWindow.open(mapMarker);

    if (this.closeClickSubscription) {
      this.closeClickSubscription.unsubscribe();
      this.closeClickSubscription = null;
    }

    this.closeClickSubscription = this.infoWindow.closeclick.subscribe(() => {
      this.infoWindowOpen = false;
      this.tempSelectedMarkerOrder = null;

      if (setId && this.selectedMarkerName === '') {
        this.deleteMarker(this.selectedMarkerIndex);
      }
    });
  }

  async removeMarker(index: number) {
    let deleteTravel: boolean = (await this.message.Warning(
      'Advertencia',
      '¿Desea Eliminar el marcador?',
      true,
      'Sí',
      'No'
    )) as boolean;
    if (deleteTravel) {
      this.deleteMarker(index);
    }
    this.infoWindowOpen = false;
  }

  async deleteMarker(index: number) {
    this.markers.splice(index, 1);
    this.circles.splice(index, 1);
    this.directionsResults = undefined;
    this.infoWindowOpen = false;
  }

  normalizeString(str: string): string {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  saveMarkerLabel() {
    if (!this.selectedMarkerName.trim()) {
      this.message.Warning(
        'Error',
        'El nombre del marcador no puede estar vacío.'
      );
      return;
    }

    const isDuplicate = this.markers.some((marker, index) => {
      const normalizedMarkerId = this.normalizeString(marker.id);
      const normalizedSelectedMarkerName = this.normalizeString(
        this.selectedMarkerName
      );
      return (
        index !== this.selectedMarkerIndex &&
        normalizedMarkerId === normalizedSelectedMarkerName
      );
    });

    if (isDuplicate) {
      this.message.Warning('Error', 'El nombre del marcador ya existe.');
      this.selectedMarkerName = '';
      return;
    }

    let marcadores = JSON.parse(JSON.stringify(this.markers));
    const sourceMarker: Marker = marcadores[this.selectedMarkerIndex];
    let targerMarker: Marker = null;

    if (this.tempSelectedMarkerOrder !== null) {
      targerMarker = marcadores.find(
        (marker) => marker.id === this.tempSelectedMarkerOrder
      );
      if (targerMarker) {
        [sourceMarker.position.lat, targerMarker.position.lat] = [
          targerMarker.position.lat,
          sourceMarker.position.lat,
        ];
        [sourceMarker.position.lng, targerMarker.position.lng] = [
          targerMarker.position.lng,
          sourceMarker.position.lng,
        ];
        [sourceMarker.id, targerMarker.id] = [targerMarker.id, sourceMarker.id];
      }
      this.tempSelectedMarkerOrder = null;
    }

    const marker = targerMarker || sourceMarker;
    marker.id = this.selectedMarkerName;

    this.markers = [...marcadores];
    this.reloadComboMarker.emit();
    this.infoWindow.close();
    this.infoWindowOpen = false;

    this.selectedMarkerName = marker.id;

    if (marcadores.length === 1) {
      this.message.Info('', 'Escoja el punto de destino', false, 'Continuar');
    }
  }

  changeValueMarker($event: string) {
    if ($event !== this.combosConfigMarker.selected) {
      this.tempSelectedMarkerOrder = $event;
    } else {
      this.tempSelectedMarkerOrder = null;
    }
  }

  clearRoute() {
    this.directionsResults = undefined;
    this.travelTime = undefined;
    this.travelDistance = undefined;
    this.routerFinished = false;
  }
}
